'use client';

// module imports
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface ProfileFieldProps {
  label: string;
  value: string;
  onSave: (value: string) => void;
  type?: 'text' | 'password' | 'email' | 'tel' | 'url' | 'select';
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  showEditButton?: boolean;
  customDisplay?: (value: string) => string;
  validation?: (value: string) => string | null;
  autoFocus?: boolean;
  options?: SelectOption[];
}

export default function ProfileField({ label, value, onSave, type = 'text', placeholder, disabled = false, readOnly = false, className = '', inputClassName = '', labelClassName = '', showEditButton = true, customDisplay, validation, autoFocus = false, options = [] }: ProfileFieldProps) {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setTemp(value), [value]);

  const handleSave = async () => {
    // Validate if validation function is provided
    if (validation) {
      const validationError = validation(temp);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setError(null);
    setEditing(false);
    setSaving(true);
    try {
      await onSave(temp);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTemp(value); // Reset to original value
    setEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const getDisplayValue = () => {
    if (customDisplay) {
      return customDisplay(value);
    }
    if (type === 'select' && options.length > 0) {
      const option = options.find((opt) => opt.value === value);
      return option ? option.label : value;
    }
    return value;
  };

  const displayValue = getDisplayValue();

  // If disabled or read-only, show static display
  if (disabled || readOnly) {
    return (
      <div className={`grid grid-cols-[max-content_1fr_max-content] gap-y-2 gap-x-3 sm:gap-x-6 items-center ${className}`}>
        <div className={`text-gray-400 text-sm sm:text-lg font-semibold ${labelClassName}`}>{label}</div>
        <div className="text-base sm:text-xl font-medium">{displayValue}</div>
        <div className="w-16 sm:w-20"></div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-[max-content_1fr_max-content] gap-y-2 gap-x-3 sm:gap-x-6 items-center ${className}`}>
      <div className={`text-gray-400 text-sm sm:text-lg font-semibold ${labelClassName}`}>{label}</div>
      <div className="text-base sm:text-xl font-medium">
        {editing ? (
          <div className="flex flex-col gap-1">
            {type === 'select' ? (
              <select className={`border-0 border-b border-gray-400 bg-transparent px-0 py-1 text-base sm:text-xl font-medium text-gray-700 focus:outline-none focus:border-gray-600 ${inputClassName} ${error ? 'border-red-500 focus:border-red-500' : ''}`} value={temp} autoFocus={autoFocus} onChange={(e) => setTemp(e.target.value)} onKeyDown={handleKeyDown}>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input type={type} className={`border-0 border-b border-gray-400 bg-transparent px-0 py-1 text-base sm:text-xl font-medium text-gray-700 focus:outline-none focus:border-gray-600 ${inputClassName} ${error ? 'border-red-500 focus:border-red-500' : ''}`} value={temp} placeholder={placeholder} autoFocus={autoFocus} onChange={(e) => setTemp(e.target.value)} onKeyDown={handleKeyDown} />
            )}
            {error && <span className="text-red-500 text-xs sm:text-sm">{error}</span>}
          </div>
        ) : (
          displayValue
        )}
      </div>
      <div className="w-16 sm:w-20 flex justify-end">
        {editing ? (
          <div className="flex gap-2 sm:gap-4">
            <button className="text-blue-600 text-xs sm:text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors disabled:opacity-50" onClick={handleSave} type="button" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="text-blue-600 text-xs sm:text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors disabled:opacity-50" onClick={handleCancel} type="button" disabled={saving}>
              Cancel
            </button>
          </div>
        ) : (
          showEditButton && (
            <button className="flex items-center gap-1 text-blue-600 text-xs sm:text-sm font-medium cursor-pointer hover:text-blue-800 transition-colors disabled:opacity-50" onClick={() => setEditing(true)} type="button" tabIndex={-1} disabled={saving}>
              <Pencil size={12} className="inline-block sm:w-3.5 sm:h-3.5" />
              Edit
            </button>
          )
        )}
      </div>
    </div>
  );
}
